import { type Mode } from './TripPlanner';

export interface RouteDetail {
  color: string;
  positions: { coordinates: number[][] };
}

export interface RoutePayload {
  points: number[][];
  profile: string;
  elevation: boolean;
  instructions: boolean;
  locale: string;
  points_encoded: boolean;
  snap_preventions: string[];
  details?: string[];
}

export interface RouteResponse {
  paths: [{ points: { coordinates: number[][] } }];
}

export interface MapDisplayProps {
  routes: RouteDetail[];
  source: string;
  destination: string;
}

export interface LatLng {
  lat: number;
  lng: number;
}

export interface Route {
  coordinates: LatLng[];
}

export interface ItineraryData {
  routes: Route[];
  source: LatLng;
  destination: LatLng;
}

interface CoordinateDTO {
  lat: number;
  lon: number;
}

export interface RouteParametersDTO {
  source: CoordinateDTO;
  destination: CoordinateDTO;
  type: string;
  startDateTime: number;
}

export interface ItineraryResponse {
  hints: Hints;
  info: Info;
  paths: Path[];
}

export interface ItineraryAPIResponse {
  data: UnifiedItineraryDTO;
}
export interface Path {
  distance: number;
  weight: number;
  time: number;
  transfers: number;
  points_encoded: boolean;
  bbox: number[];
  points: string;
  instructions: Instruction[];
  legs: [];
  details: Details;
  ascend: number;
  descend: number;
  snapped_waypoints: string;
  price: number;
}

interface Details {
  road_environment: Array<Array<number | string>>;
  road_class: Array<Array<number | string>>;
  max_speed: Array<Array<null | number | number>>;
  average_speed: number[][];
}

interface Instruction {
  distance: number;
  heading?: number;
  sign: number;
  interval: number[];
  text: string;
  time: number;
  street_name: string;
  street_ref?: string;
  exit_number?: number;
  exited?: boolean;
  turn_angle?: number;
  last_heading?: number;
}

interface Info {
  copyrights: string[];
  took: number;
  road_data_timestamp: string;
}

interface Hints {
  'visited_nodes.sum': number;
  'visited_nodes.average': number;
}

export enum ItineraryType {
  OTP = 'otp',
  GRAPH_HOPPER = 'graph_hopper',
  CAR = 'car',
  BLABLADAILY = 'blabladaily',
}

export enum GeometryType {
  DEUXD = '2d',
  TROISD = '3d',
}
export interface UnifiedItineraryDTO {
  duration: number; // Total duration of the trip
  startTime: string; // Start time (ISO 8601)
  endTime: string; // End time (ISO 8601)
  walkTime?: number; // Time spent walking (optional)
  cost?: number; // Cost of the itinerary (optional)
  price?: number; // Cost of the itinerary (optional)
  legs: UnifiedLegDTO[]; // Array of legs for the trip
  type: ItineraryType; // Source type (OTP or GRAPH_HOPPER)
}

export interface UnifiedLegDTO {
  mode: Mode; // Mode of transportation (e.g., walk, bus, train)
  duration: number; // Duration of this leg
  distance: number; // Distance covered in this leg
  startLocation: Location; // Start location details
  endLocation: Location; // End location details
  from?: {
    stop: {
      id: string;
      gtfsId?: string;
    };
    delay?: number;
    dock?: string;
    name: string;
    lon?: number;
    lat?: number;
    departure?: number;
    vehicleRentalStation?: {
      rentalUris: {
        android: string;
        ios: string;
        web: string;
      } | null;
    } | null;
  };
  to?: {
    stop: {
      id: string;
      gtfsId?: string;
    };
    delay?: number;
    dock?: string;
    name: string;
    lon?: number;
    lat?: number;
    departure?: number;
    vehicleRentalStation?: {
      rentalUris: {
        android: string;
        ios: string;
        web: string;
      } | null;
    } | null;
  };
  startTime: string;
  endTime: string;
  headsign?: string; // Destination sign (optional)
  geometry?: string; // Encoded geometry string (optional)
  geometryType?: GeometryType; // Geometry type (2D or 3D)
  agency?: { id: string; gtfsId?: string }; // Transport agency details (optional)
  intermediateStops?: IntermediateStopsDTO[]; // Stops during the leg (optional)
  routeShortName?: string;
  realTime?: boolean;
}

export interface IntermediateStopsDTO {
  name: string;
  stopId: string;
  gtfsId?: string;
}

export interface Location {
  lat: string;
  lon: string;
}
